import React, { useState, useEffect } from 'react';
import { payments, auth } from '../api';
import pricingConfig from '../config/pricing.json';

function Payment() {
  const [balance, setBalance] = useState(0);
  const [tariffInfo, setTariffInfo] = useState(null);
  const [resourcesStats, setResourcesStats] = useState(null);
  const [purchasedServices, setPurchasedServices] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentsList, setPaymentsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [cancelCurrentTariff, setCancelCurrentTariff] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  // Форма оплаты
  const [amount, setAmount] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  useEffect(() => {
    loadData();
    
    // Проверяем, нужно ли показать форму оплаты из URL параметра
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showForm') === 'true') {
      setShowPaymentForm(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadData = async () => {
    try {
      const [balanceResponse, tariffResponse, statsResponse, servicesResponse] = await Promise.all([
        auth.getMe(),
        auth.getTariffInfo(),
        auth.getResourcesStats(),
        auth.getPurchasedServices()
      ]);
      setBalance(balanceResponse.data.balance || 0);
      setTariffInfo(tariffResponse.data);
      setResourcesStats(statsResponse.data);
      setPurchasedServices(servicesResponse.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setLoadError(true);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await payments.getAll();
      setPaymentsList(response.data);
    } catch (err) {
      console.error('Error loading payments:', err);
      setLoadError(true);
    }
  };

  const retryLoad = () => {
    setLoadError(false);
    setInitialLoading(true);
    loadData();
    loadPayments();
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!amount || parseFloat(amount) <= 0) {
      setError('Введите корректную сумму');
      return;
    }

    if (!receiptFile) {
      setError('Прикрепите файл чека');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('amount', parseFloat(amount));
      formData.append('receipt_file', receiptFile);

      await payments.create(formData);
      setSuccess('Платеж успешно создан и отправлен на обработку');
      setAmount('');
      setReceiptFile(null);
      setFilePreview(null);
      setShowPaymentForm(false);
      await loadPayments();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при создании платежа');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseTariff = async (tariffKey) => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await auth.purchaseTariff({
        tariff: tariffKey,
        cancel_current: cancelCurrentTariff
      });
      
      setSuccess(`Тариф "${pricingConfig.tariffs[tariffKey].name}" успешно активирован!`);
      setSelectedTariff(null);
      setCancelCurrentTariff(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при покупке тарифа');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseService = (serviceType) => {
    // Открываем модальное окно подтверждения
    setSelectedService(serviceType);
  };

  const confirmPurchaseService = async () => {
    if (!selectedService) return;
    
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await auth.purchaseService({
        service_type: selectedService
      });
      
      if (selectedService === 'question_bank_creation') {
        setSuccess(response.data.message || 'Информация отправлена');
        setSelectedService(null);
      } else {
        const expiresAt = response.data.expires_at ? new Date(response.data.expires_at).toLocaleDateString('ru-RU') : '';
        setSuccess(`Услуга успешно приобретена! Действует до ${expiresAt}`);
        setSelectedService(null);
        await loadData();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при покупке услуги');
    } finally {
      setLoading(false);
    }
  };

  const getServiceInfo = (serviceType) => {
    const serviceNames = {
      question_bank: 'База вопросов',
      test_schema: 'Тест-схема',
      group_invitation: 'Групповая ссылка',
      question_bank_creation: 'Составление базы вопросов'
    };
    
    const prices = {
      question_bank: pricingConfig.over_limit_prices.question_bank.price,
      test_schema: pricingConfig.over_limit_prices.test_schema.price,
      group_invitation: pricingConfig.over_limit_prices.group_invitation.price,
      question_bank_creation: pricingConfig.question_bank.price_per_300_questions || pricingConfig.question_bank.price_per_100_questions || 5000
    };
    
    // Рассчитываем дату окончания (1 месяц от текущей даты)
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + 1);
    
    return {
      name: serviceNames[serviceType] || serviceType,
      price: prices[serviceType] || 0,
      expiresAt: expiresAt
    };
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: { label: 'Ожидает обработки', color: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Принят', color: 'bg-green-100 text-green-800' },
      approved_custom: { label: 'Принят с изменением', color: 'bg-blue-100 text-blue-800' },
      rejected: { label: 'Отклонен', color: 'bg-red-100 text-red-800' },
    };
    return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Бессрочно';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Определяем иерархию тарифов
  const tariffHierarchy = { free: 0, hr: 1, standard: 2, unlimited: 3, unlimited_plus: 4 };
  const currentTariffLevel = tariffInfo ? tariffHierarchy[tariffInfo.tariff] || 0 : 0;

  // Получаем информацию о текущем тарифе
  const currentTariff = tariffInfo ? pricingConfig.tariffs[tariffInfo.tariff] : null;
  const currentTariffLimits = currentTariff?.limits || {};

  // Тарифы для отображения — по возрастанию цены (сам pricing.json не меняем, его читает backend)
  const sortedTariffEntries = Object.entries(pricingConfig.tariffs).sort(
    (a, b) => (a[1].price || 0) - (b[1].price || 0)
  );
  // Рекомендуемый тариф: Standard, иначе средний по цене платный
  const paidTariffEntries = sortedTariffEntries.filter(([, t]) => t.price > 0);
  const recommendedTariffKey = pricingConfig.tariffs.standard
    ? 'standard'
    : (paidTariffEntries[Math.floor((paidTariffEntries.length - 1) / 2)] || [])[0];

  if (initialLoading || loadError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Оплата услуг
          </h1>
          <p className="text-gray-600">
            Управление тарифами, покупка услуг и пополнение баланса
          </p>
        </div>
        {initialLoading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <span>Не удалось загрузить данные</span>
            <button onClick={retryLoad} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Оплата услуг
          </h1>
          <p className="text-gray-600">
            Управление тарифами, покупка услуг и пополнение баланса
          </p>
        </div>

        {/* Баланс */}
        <div className="bg-blue-600 rounded-lg shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Текущий баланс</p>
              <p className="text-4xl font-bold">{formatAmount(balance)}</p>
            </div>
          </div>
        </div>

        {/* Текущий тариф */}
        {tariffInfo && currentTariff && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Ваш текущий тариф</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl font-bold text-blue-600">{currentTariff.name}</span>
                  {tariffInfo.tariff !== 'free' && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                      Активен
                    </span>
                  )}
                </div>
                {tariffInfo.tariff_expires_at && (
                  <p className="text-gray-600 mb-2">
                    <strong>Действует до:</strong> {formatDate(tariffInfo.tariff_expires_at)}
                  </p>
                )}
                {tariffInfo.tariff === 'free' && (
                  <p className="text-gray-600 mb-2">
                    <strong>Тариф:</strong> Бессрочный
                  </p>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Доступные ресурсы:</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>
                    Баз вопросов: <strong>{resourcesStats?.question_banks || 0}</strong> / {currentTariffLimits.question_banks === null ? '∞' : currentTariffLimits.question_banks}
                    {resourcesStats?.purchased_question_banks > 0 && (
                      <span className="ml-2 text-green-600 font-semibold">+{resourcesStats.purchased_question_banks} докуплено</span>
                    )}
                  </li>
                  <li>
                    Тест-схем: <strong>{resourcesStats?.test_schemas || 0}</strong> / {currentTariffLimits.test_schemas === null ? '∞' : currentTariffLimits.test_schemas}
                    {resourcesStats?.purchased_test_schemas > 0 && (
                      <span className="ml-2 text-green-600 font-semibold">+{resourcesStats.purchased_test_schemas} докуплено</span>
                    )}
                  </li>
                  <li>
                    Групповых ссылок: <strong>{resourcesStats?.group_invitations || 0}</strong> / {currentTariffLimits.group_invitations === null || currentTariffLimits.group_invitations === undefined ? '∞' : currentTariffLimits.group_invitations}
                    {resourcesStats?.purchased_group_invitations > 0 && (
                      <span className="ml-2 text-green-600 font-semibold">+{resourcesStats.purchased_group_invitations} докуплено</span>
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно для сообщений об ошибках и успехе */}
        {(error || success) && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => {
            setError('');
            setSuccess('');
          }}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-xl font-bold ${error ? 'text-red-900' : 'text-green-900'}`}>
                  {error ? 'Ошибка' : 'Успешно'}
                </h3>
                <button
                  onClick={() => {
                    setError('');
                    setSuccess('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className={`mb-6 ${error ? 'text-red-700' : 'text-green-700'}`}>
                {error || success}
              </p>
              <button
                onClick={() => {
                  setError('');
                  setSuccess('');
                }}
                className={`w-full px-4 py-2 rounded-lg font-semibold transition-all ${
                  error 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                Понятно
              </button>
            </div>
          </div>
        )}

        {/* Тарифы */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Доступные тарифы</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedTariffEntries.map(([key, tariff]) => {
              const tariffLevel = tariffHierarchy[key] || 0;
              const isCurrentTariff = tariffInfo?.tariff === key;
              const isDisabled = tariffLevel < currentTariffLevel && currentTariffLevel > 0;
              const isRecommended = key === recommendedTariffKey;

              return (
                <div
                  key={key}
                  className={`relative bg-white p-6 rounded-xl border-2 ${
                    isCurrentTariff
                      ? 'border-blue-500 shadow-lg'
                      : isDisabled
                      ? 'border-gray-200 opacity-50'
                      : isRecommended
                      ? 'border-blue-600 hover:shadow-lg'
                      : 'border-gray-200 hover:shadow-lg'
                  } transition-all`}
                >
                  {isRecommended && (
                    <span className="bg-blue-600 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full absolute -top-2.5 left-1/2 -translate-x-1/2">
                      Популярный
                    </span>
                  )}
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tariff.name}</h3>
                    <div className="text-3xl font-bold text-blue-600 mb-1">
                      {tariff.price === 0 ? 'Бесплатно' : `${tariff.price}₽`}
                    </div>
                    {tariff.duration_days && (
                      <div className="text-sm text-gray-500">на {tariff.duration_days} дней</div>
                    )}
                    {!tariff.duration_days && tariff.price === 0 && (
                      <div className="text-sm text-gray-500">бессрочно</div>
                    )}
                  </div>
                  <ul className="space-y-2 text-sm text-gray-600 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span>
                      <span>
                        <strong>{tariff.limits.question_banks === null ? '∞' : tariff.limits.question_banks}</strong> баз вопросов
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span>
                      <span>
                        <strong>{tariff.limits.test_schemas === null ? '∞' : tariff.limits.test_schemas}</strong> тест-схем
                      </span>
                    </li>
                    {tariff.limits.group_invitations !== null && (
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span>
                        <span>
                          <strong>{tariff.limits.group_invitations}</strong> групповых ссылок
                        </span>
                      </li>
                    )}
                    {key === 'unlimited_plus' && (
                      <li className="flex items-start">
                        <span className="text-green-500 mr-2"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span>
                        <span>
                          <strong>Разработка базы</strong> на 300 вопросов включена
                        </span>
                      </li>
                    )}
                  </ul>
                  {isCurrentTariff ? (
                    <div className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold text-center">
                      Текущий тариф
                    </div>
                  ) : isDisabled ? (
                    <div className="w-full bg-gray-100 text-gray-500 px-4 py-2 rounded-lg text-sm font-semibold text-center cursor-not-allowed">
                      Недоступно
                    </div>
                  ) : tariff.price === 0 ? (
                    <div className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold text-center">
                      Бесплатный тариф
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setSelectedTariff(key);
                        setCancelCurrentTariff(false);
                      }}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg transition-all"
                      disabled={loading}
                    >
                      Купить тариф
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Модальное окно покупки тарифа */}
        {selectedTariff && tariffInfo && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Покупка тарифа "{pricingConfig.tariffs[selectedTariff].name}"
              </h3>
              
              {tariffInfo.tariff !== 'free' && tariffInfo.tariff_expires_at && new Date(tariffInfo.tariff_expires_at) > new Date() && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 mb-2">
                    Ваш текущий тариф "{pricingConfig.tariffs[tariffInfo.tariff].name}" действует до {formatDate(tariffInfo.tariff_expires_at)}
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!cancelCurrentTariff}
                        onChange={() => setCancelCurrentTariff(false)}
                        className="mt-1"
                      />
                      <div className="text-sm text-blue-800">
                        <strong>Продлить после окончания текущего тарифа</strong>
                        <p className="text-xs text-blue-600 mt-1">
                          Новый тариф начнется после {formatDate(tariffInfo.tariff_expires_at)}
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={cancelCurrentTariff}
                        onChange={() => setCancelCurrentTariff(true)}
                        className="mt-1"
                      />
                      <div className="text-sm text-blue-800">
                        <strong>Отказаться от текущего тарифа и перейти сразу</strong>
                        <p className="text-xs text-blue-600 mt-1">
                          Деньги за неиспользованный период вернутся на баланс
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <p className="text-gray-700">
                  Стоимость: <strong className="text-lg">{pricingConfig.tariffs[selectedTariff].price}₽</strong>
                </p>
                {cancelCurrentTariff && tariffInfo.tariff !== 'free' && tariffInfo.tariff_expires_at && (
                  <p className="text-sm text-green-600 mt-2">
                    Вам будет возвращена часть средств за неиспользованный период текущего тарифа
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedTariff(null);
                    setCancelCurrentTariff(false);
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={() => handlePurchaseTariff(selectedTariff)}
                  disabled={loading || balance < pricingConfig.tariffs[selectedTariff].price}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Обработка...' : 'Купить'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно подтверждения покупки услуги */}
        {selectedService && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Подтверждение покупки услуги
              </h3>
              
              {selectedService === 'question_bank_creation' ? (
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    Вы заказываете услугу <strong>"{getServiceInfo(selectedService).name}"</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Для заказа услуги составления базы вопросов свяжитесь с нами по email: skillgate.pro@gmail.com
                  </p>
                </div>
              ) : (
                <div className="mb-6 space-y-4">
                  <div>
                    <p className="text-gray-700 mb-2">
                      Вы покупаете услугу: <strong>{getServiceInfo(selectedService).name}</strong>
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Стоимость услуги:</span>
                      <span className="font-semibold text-gray-900">{getServiceInfo(selectedService).price}₽</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Текущий баланс:</span>
                      <span className="font-semibold text-gray-900">{formatAmount(balance)}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 flex justify-between">
                      <span className="text-gray-900 font-semibold">Будет списано:</span>
                      <span className={`font-bold ${balance >= getServiceInfo(selectedService).price ? 'text-green-600' : 'text-red-600'}`}>
                        {getServiceInfo(selectedService).price}₽
                      </span>
                    </div>
                    {balance < getServiceInfo(selectedService).price && (
                      <p className="text-xs text-red-600 mt-2">
                        Недостаточно средств на балансе
                      </p>
                    )}
                    <div className="border-t border-gray-300 pt-2 flex justify-between">
                      <span className="text-gray-600">Остаток после покупки:</span>
                      <span className="font-semibold text-gray-900">
                        {formatAmount(Math.max(0, balance - getServiceInfo(selectedService).price))}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <strong>Срок действия:</strong> услуга будет действовать до{' '}
                      <strong>{getServiceInfo(selectedService).expiresAt.toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}</strong> (1 месяц с момента покупки)
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedService(null);
                    setError('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Отмена
                </button>
                {selectedService !== 'question_bank_creation' && (
                  <button
                    onClick={confirmPurchaseService}
                    disabled={loading || balance < getServiceInfo(selectedService).price}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Обработка...' : 'Подтвердить покупку'}
                  </button>
                )}
                {selectedService === 'question_bank_creation' && (
                  <button
                    onClick={confirmPurchaseService}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Обработка...' : 'Отправить запрос'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Докупленные услуги */}
        {purchasedServices && purchasedServices.filter(s => {
          const now = new Date();
          const expiresAt = new Date(s.expires_at);
          return expiresAt > now && s.is_active;
        }).length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Докупленные услуги</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {purchasedServices.filter(s => {
                const now = new Date();
                const expiresAt = new Date(s.expires_at);
                return expiresAt > now && s.is_active;
              }).map((service) => {
                const serviceNames = {
                  question_bank: 'База вопросов',
                  test_schema: 'Тест-схема',
                  group_invitation: 'Групповая ссылка'
                };
                const expiresAt = new Date(service.expires_at);
                const now = new Date();
                const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                
                return (
                  <div key={service.id} className="bg-green-50 border-2 border-green-300 rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{serviceNames[service.service_type] || service.service_type}</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Куплено: {formatDate(service.created_at)}
                    </p>
                    <p className="text-sm font-semibold text-green-700 mb-2">
                      Действует до: {formatDate(service.expires_at)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Осталось дней: {daysLeft > 0 ? daysLeft : 0}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Отдельные услуги */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Дополнительные услуги</h2>
          <p className="text-sm text-gray-600 mb-4">Услуги действуют 1 месяц с момента покупки</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">+1 База вопросов</h3>
              <p className="text-2xl font-bold text-blue-600 mb-4">
                {pricingConfig.over_limit_prices.question_bank.price}₽
              </p>
              <button
                onClick={() => handlePurchaseService('question_bank')}
                disabled={loading || balance < pricingConfig.over_limit_prices.question_bank.price}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Купить
              </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">+1 Тест-схема</h3>
              <p className="text-2xl font-bold text-blue-600 mb-4">
                {pricingConfig.over_limit_prices.test_schema.price}₽
              </p>
              <button
                onClick={() => handlePurchaseService('test_schema')}
                disabled={loading || balance < pricingConfig.over_limit_prices.test_schema.price}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Купить
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">+1 Групповая ссылка</h3>
              <p className="text-2xl font-bold text-blue-600 mb-4">
                {pricingConfig.over_limit_prices.group_invitation.price}₽
              </p>
              <button
                onClick={() => handlePurchaseService('group_invitation')}
                disabled={loading || balance < pricingConfig.over_limit_prices.group_invitation.price}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Купить
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Составление базы вопросов</h3>
              <p className="text-2xl font-bold text-blue-600 mb-4">
                {pricingConfig.question_bank.price_per_300_questions || pricingConfig.question_bank.price_per_100_questions || 5000}₽
              </p>
              <p className="text-sm text-gray-600 mb-4">за 300 вопросов</p>
              <button
                onClick={() => handlePurchaseService('question_bank_creation')}
                disabled={loading}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Заказать
              </button>
            </div>
          </div>
      </div>

        {/* Пополнение баланса */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Пополнение баланса
          </h2>
          
                {!showPaymentForm && (
                  <button
                    onClick={() => setShowPaymentForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
                  >
                    Внести платёж
                  </button>
                )}

            {showPaymentForm && (
              <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Форма оплаты</h3>
                  <button
                    onClick={() => {
                      setShowPaymentForm(false);
                      setError('');
                      setSuccess('');
                      setAmount('');
                      setReceiptFile(null);
                      setFilePreview(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        QR-код для оплаты
                      </label>
                      <div className="bg-white p-4 rounded-lg border border-gray-300 flex justify-center">
                        <img
                          src="/qr-code.png"
                          alt="QR код для оплаты"
                          className="max-w-full h-auto"
                          style={{ maxWidth: '300px' }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Отсканируйте QR-код для оплаты через СБП
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Вносимая сумма (руб.)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="5000"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Файл-подтверждение оплаты (JPG, PNG или PDF)
                        </label>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleFileChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                        {filePreview && (
                          <div className="mt-3">
                            <img
                              src={filePreview}
                              alt="Preview"
                              className="max-w-xs rounded-lg border border-gray-300"
                            />
                          </div>
                        )}
                        {receiptFile && !filePreview && (
                          <p className="mt-2 text-sm text-gray-600">
                            Выбран файл: {receiptFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Отправка...' : 'Оплата произведена'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPaymentForm(false);
                        setError('');
                        setSuccess('');
                        setAmount('');
                        setReceiptFile(null);
                        setFilePreview(null);
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Реквизиты для оплаты</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div><strong>Получатель:</strong> ИП Вихляев Сергей Николаевич</div>
              <div><strong>ИНН:</strong> 682004283576</div>
              <div><strong>Способ оплаты:</strong> Система быстрых платежей (СБП)</div>
            </div>
          </div>
        </div>

        {/* История платежей */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              История платежей
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сумма
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Комментарий
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Чек
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentsList.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      Платежей пока нет
                    </td>
                  </tr>
                ) : (
                  paymentsList.map((payment) => {
                    const statusInfo = getStatusLabel(payment.status);
                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(payment.created_at)}
                          {payment.payment_id && (
                            <div className="text-xs text-gray-500 mt-1">
                              ID: {payment.payment_id}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{formatAmount(payment.amount)}</div>
                            {payment.approved_amount && payment.approved_amount !== payment.amount && (
                              <div className="text-xs text-gray-500">
                                Одобрено: {formatAmount(payment.approved_amount)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {payment.admin_comment || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(payment.receipt_filename || payment.payment_id) && (
                            <a
                              href={`${process.env.REACT_APP_API_URL || (window.location.hostname === 'skillgate.pro' || window.location.hostname === 'www.skillgate.pro' ? 'https://api1.skillgate.pro' : 'http://localhost:8000')}/api/payments/receipt/${payment.payment_id || payment.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              Скачать чек
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Payment;
